import { create } from 'zustand'
import type { LngLat } from '../lib/geo'
import type { Segment, Sport, Waypoint } from '../lib/models'
import {
  computeRoute,
  computeRoundTrip,
  estimateDurationS,
  fetchRouteExtras,
  gradeDifficulty,
  type Fitness,
  type RouteResult,
  RoutingError,
} from '../lib/routing'
import { elevationForLine, type ElevationProfile } from '../lib/elevation'
import { reverseGeocode } from '../lib/geocode'

interface PlannerState {
  sport: Sport
  fitness: Fitness
  waypoints: Waypoint[]
  route: RouteResult | null
  profile: ElevationProfile | null
  extras: { surfaces: Segment[]; waytypes: Segment[] } | null
  busy: boolean
  error: string | null
  /** distance along the route currently hovered in the elevation chart */
  hoverAtM: number | null
  undoStack: Waypoint[][]

  setSport: (s: Sport) => void
  setFitness: (f: Fitness) => void
  addWaypoint: (p: LngLat, name?: string) => void
  insertWaypoint: (index: number, p: LngLat) => void
  moveWaypoint: (index: number, p: LngLat) => void
  /** call when a drag gesture ends — re-labels the waypoint */
  finishMove: (index: number) => void
  removeWaypoint: (index: number) => void
  setWaypoints: (w: Waypoint[], sport?: Sport) => void
  reverse: () => void
  backToStart: () => void
  clear: () => void
  undo: () => void
  roundTrip: (targetKm: number) => Promise<void>
  setHoverAtM: (m: number | null) => void
}

let abort: AbortController | null = null
let extrasAbort: AbortController | null = null
let extrasTimer: ReturnType<typeof setTimeout> | undefined
let recomputeTimer: ReturnType<typeof setTimeout> | undefined

async function recompute(set: (partial: Partial<PlannerState>) => void, get: () => PlannerState): Promise<void> {
  abort?.abort()
  extrasAbort?.abort()
  clearTimeout(extrasTimer)
  const { waypoints, sport } = get()
  if (waypoints.length < 2) {
    set({ route: null, profile: null, extras: null, busy: false, error: null })
    return
  }
  const ctl = new AbortController()
  abort = ctl
  set({ busy: true, error: null })
  try {
    const route = await computeRoute(
      waypoints.map((w) => [w.lng, w.lat] as LngLat),
      sport,
      ctl.signal,
    )
    if (ctl.signal.aborted) return
    set({ route, extras: null })

    const ele = await elevationForLine(route.coords, ctl.signal).catch(() => null)
    if (ctl.signal.aborted) return
    if (ele) set({ route: { ...route, coords: ele.coords }, profile: ele.profile, busy: false })
    else set({ profile: null, busy: false })

    // surfaces/waytypes are heavier — fetch once the route settles
    extrasTimer = setTimeout(async () => {
      extrasAbort = new AbortController()
      const coords = get().route?.coords
      if (!coords) return
      const extras = await fetchRouteExtras(coords, sport, extrasAbort.signal).catch(() => null)
      if (extras && !extrasAbort.signal.aborted) set({ extras })
    }, 800)
  } catch (e) {
    if (ctl.signal.aborted) return
    set({
      busy: false,
      route: null,
      profile: null,
      extras: null,
      error: e instanceof RoutingError ? e.message : 'routing unavailable — check your connection',
    })
  }
}

function scheduleRecompute(set: (p: Partial<PlannerState>) => void, get: () => PlannerState, delay = 150): void {
  clearTimeout(recomputeTimer)
  recomputeTimer = setTimeout(() => void recompute(set, get), delay)
}

/** Give an unnamed waypoint a human place name (async, keeps UX snappy). */
function labelWaypoint(set: (p: Partial<PlannerState>) => void, get: () => PlannerState, lng: number, lat: number): void {
  void reverseGeocode(lng, lat)
    .then((place) => {
      if (!place) return
      const w = get().waypoints
      const idx = w.findIndex((x) => !x.name && Math.abs(x.lng - lng) < 1e-9 && Math.abs(x.lat - lat) < 1e-9)
      if (idx === -1) return
      const next = [...w]
      next[idx] = { ...next[idx], name: place.name }
      set({ waypoints: next })
    })
    .catch(() => {})
}

export const usePlanner = create<PlannerState>((set, get) => {
  const pushUndo = () => {
    const stack = get().undoStack
    set({ undoStack: [...stack.slice(-29), get().waypoints] })
  }

  return {
    sport: 'hike',
    fitness: 'average',
    waypoints: [],
    route: null,
    profile: null,
    extras: null,
    busy: false,
    error: null,
    hoverAtM: null,
    undoStack: [],

    setSport: (sport) => {
      set({ sport })
      scheduleRecompute(set, get, 0)
    },
    setFitness: (fitness) => set({ fitness }),

    addWaypoint: (p, name) => {
      pushUndo()
      set({ waypoints: [...get().waypoints, { lng: p[0], lat: p[1], name }] })
      if (!name) labelWaypoint(set, get, p[0], p[1])
      scheduleRecompute(set, get, 0)
    },

    insertWaypoint: (index, p) => {
      pushUndo()
      const w = [...get().waypoints]
      w.splice(index, 0, { lng: p[0], lat: p[1] })
      set({ waypoints: w })
      labelWaypoint(set, get, p[0], p[1])
      scheduleRecompute(set, get, 0)
    },

    moveWaypoint: (index, p) => {
      const w = [...get().waypoints]
      w[index] = { ...w[index], lng: p[0], lat: p[1], name: undefined }
      set({ waypoints: w })
      scheduleRecompute(set, get, 300)
    },

    finishMove: (index) => {
      const w = get().waypoints[index]
      if (w && !w.name) labelWaypoint(set, get, w.lng, w.lat)
    },

    removeWaypoint: (index) => {
      pushUndo()
      set({ waypoints: get().waypoints.filter((_, i) => i !== index) })
      scheduleRecompute(set, get, 0)
    },

    setWaypoints: (waypoints, sport) => {
      set({ waypoints, ...(sport ? { sport } : {}), undoStack: [] })
      scheduleRecompute(set, get, 0)
    },

    reverse: () => {
      pushUndo()
      set({ waypoints: [...get().waypoints].reverse() })
      scheduleRecompute(set, get, 0)
    },

    backToStart: () => {
      const w = get().waypoints
      if (w.length < 2) return
      pushUndo()
      set({ waypoints: [...w, w[0]] })
      scheduleRecompute(set, get, 0)
    },

    clear: () => {
      abort?.abort()
      set({ waypoints: [], route: null, profile: null, extras: null, error: null, busy: false, undoStack: [] })
    },

    undo: () => {
      const stack = get().undoStack
      if (!stack.length) return
      set({ waypoints: stack[stack.length - 1], undoStack: stack.slice(0, -1) })
      scheduleRecompute(set, get, 0)
    },

    roundTrip: async (targetKm) => {
      const start = get().waypoints[0]
      if (!start) return
      set({ busy: true, error: null })
      try {
        const bearing = Math.floor(Math.random() * 360)
        const { via } = await computeRoundTrip([start.lng, start.lat], targetKm * 1000, get().sport, bearing)
        pushUndo()
        set({
          waypoints: [start, ...via.map((v) => ({ lng: v[0], lat: v[1] })), start],
        })
        scheduleRecompute(set, get, 0)
      } catch {
        set({ busy: false, error: 'could not generate a round trip here' })
      }
    },

    setHoverAtM: (hoverAtM) => set({ hoverAtM }),
  }
})

/** Derived headline stats for the current plan. */
export function plannerStats(s: PlannerState) {
  if (!s.route) return null
  const ascent = s.profile?.ascentM ?? 0
  return {
    distanceM: s.route.distanceM,
    ascentM: ascent,
    descentM: s.profile?.descentM ?? 0,
    durationS: estimateDurationS(s.sport, s.route.distanceM, ascent, s.fitness),
    difficulty: gradeDifficulty(s.sport, s.route.distanceM, ascent),
  }
}
